<script lang="ts">
	import { trackOnwardClick } from '#lib/features/analytics/utils/trackOnwardClick/trackOnwardClick.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { ArtistsMixpanelEvents } from '#lib/websites/artists/constants/mixpanel-events.js';
	import { Link } from '$global/components/Link';
	import { Txt, type TxtVariant } from '$global/components/Txt';

	interface Props {
		name: string;
		slug: string;
		// When true, the artist's page is hidden: render the name as plain text
		// instead of a link to the artist page.
		hideArtistPage?: boolean | null;
		dataCy?: string;
		// Optional <Txt> variant/class applied to the rendered name. When omitted,
		// the name is rendered as inline text.
		txtVariant?: TxtVariant;
		class?: string;
		// Names the surface this link sits on for `onward_click` (HB-967) — the
		// same component is used from Editions, Shop and Primary PDPs, and the
		// journey only reads correctly if they are distinguishable.
		sourceElement?: string;
		// The event the crossing is reported under. Defaults to the artists event
		// since the destination is always an artist page; PDPs that would rather
		// file the click under their own section pass their own.
		eventName?: string;
	}

	let {
		name,
		slug,
		hideArtistPage = false,
		dataCy,
		txtVariant,
		class: className,
		sourceElement = 'artist-link',
		eventName = ArtistsMixpanelEvents.OnwardClick,
	}: Props = $props();

	const href = $derived(`${GlobalRoutes.ArtistsHome}/${slug}`);

	// An artwork PDP → the artist's own page is the "artist page → collection →
	// artwork" journey read in reverse, and one of the crossings the ticket names.
	// `from_section` is left to resolve from the current page so this single
	// component reports correctly from every PDP that renders it.
	const handleClick = () => {
		trackOnwardClick({
			eventName,
			href,
			sourceElement,
			targetType: 'artist',
			artist: name,
		});
	};
</script>

{#snippet artistName()}
	{#if txtVariant || className}
		<!-- Rendered as a <span> (not the variant's default heading element) so it
		stays valid phrasing content inside the inline <a>/<span> wrapper. -->
		<Txt component="span" variant={txtVariant} class={className}>{name}</Txt>
	{:else}{name}{/if}
{/snippet}

{#if hideArtistPage}
	<span class="uppercase" data-cy={dataCy}>{@render artistName()}</span>
{:else}
	<Link class="uppercase underline" {dataCy} {href} onclick={handleClick}
		>{@render artistName()}</Link
	>
{/if}
